import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { LOCAL_STORAGE_KEY } from 'src/app/config';
import { LxmAppModule, ReportFileType, ReportStatus, UserAction } from 'src/app/enum';
import { isSalesReportViewable } from 'src/app/enum/report-status';
import { toReportDay } from 'src/app/cards/reports/reports-date.utils';
import { AuthenticationService, SignalRService, TitleService } from 'src/app/_services';
import { LocalStorage, LxmMessage } from 'src/app/_helpers';
import { ILocationSimple, ISalesReportDto, ISalesReportSearchData } from 'src/app/_services/reports.service';
import { SalesReportSearchCard } from 'src/app/cards/reports/sales-report-search/sales-report-search.card';

@Component({
  selector: 'sales-report-layout',
  templateUrl: './sales-report.layout.html',
  host: { class: 'content' },
  styleUrls: ['./sales-report.layout.scss']
})
export class SalesReportLayout implements OnInit, OnDestroy {
  @ViewChild('searchCard') public searchCard: SalesReportSearchCard;

  public isLoading = false;
  public isReady = false;
  public search: Observable<ISalesReportSearchData>;
  public searchSubject = new BehaviorSubject<ISalesReportSearchData>(null);
  public searchPanelLocalStorageKey = LOCAL_STORAGE_KEY.REPORTS_SEARCH_PANEL_OPEN;
  public isSearchPanelOpen = true;

  private title = 'cards.reports.sales_stock.heading';
  private _report: ISalesReportDto | null;
  private _hubConnection: HubConnection;
  private _signalRSubscription: Subscription;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _authService: AuthenticationService,
    private _signalRService: SignalRService,
    private _titleService: TitleService,
    private _message: LxmMessage,
    private _cd: ChangeDetectorRef
  ) {
    this._report = this._route.snapshot.data.report || null;
    this.isReady = isSalesReportViewable(this._report?.status);
    this.isLoading = !!this._report && !this.isReady;

    const stored = LocalStorage.getValue(this.searchPanelLocalStorageKey);
    this.isSearchPanelOpen = stored === null || stored === undefined ? true : stored;
    this.search = this.searchSubject.asObservable();
  }

  public ngOnInit() {
    this._titleService.setTitle(this.title);
    this._connectSignalR();
  }

  public ngOnDestroy() {
    if (this._signalRSubscription) {
      this._signalRSubscription.unsubscribe();
    }

    if (this._hubConnection && this._report?.id) {
      this._hubConnection.send('leaveSalesReport', this._report.id);
      this._hubConnection.off('salesReportGenerating');
      this._hubConnection.off('salesReportReady');
      this._hubConnection.off('salesReportExpired');
    }
  }

  public get canAccess(): boolean {
    if (!this._authService.hasModule(LxmAppModule.Reports)) {
      return false;
    }

    if (!this._report) {
      return this.canCreate;
    }

    return this._authService.isUserOwner(this._report.createdBy?.id)
      ? this._authService.hasRight([UserAction.ViewReports])
      : this._authService.hasRight([UserAction.ViewOthersReports]);
  }

  public get canCreate(): boolean {
    return this._authService.hasRight([UserAction.CreateReports]);
  }

  public get canManage(): boolean {
    if (!this._report || this._report.status === ReportStatus.Expired) {
      return false;
    }

    return this._authService.isUserOwner(this._report.createdBy?.id)
      ? this._authService.hasRight([UserAction.ManageReports])
      : this._authService.hasRight([UserAction.ManageOthersReports]);
  }

  public get canExpand(): boolean {
    return this.canManage && this.hasRoomToExpand(this._report);
  }

  private hasRoomToExpand(report: ISalesReportDto | null): boolean {
    if (!report?.formData) {
      return false;
    }

    const selected = report.selectedDataFilters;
    const formData = report.formData;
    const min = toReportDay(formData.minReportDate);
    const max = toReportDay(formData.maxReportDate);
    const from = toReportDay(selected?.from);
    const to = toReportDay(selected?.to);

    if (from && min && from.isAfter(min, 'day')) {
      return true;
    }
    if (to && max && to.isBefore(max, 'day')) {
      return true;
    }

    const availableFileTypes = formData.fileTypes?.length
      ? formData.fileTypes
      : [
          ReportFileType.Selver_SalesReport_SalesFile,
          ReportFileType.Selver_SalesReport_InventoryFile
        ];
    const selectedFileTypes = selected?.fileTypes || [];
    if (availableFileTypes.some((fileType) => !selectedFileTypes.includes(fileType))) {
      return true;
    }

    const availableLocations = formData.locations || [];
    if (!availableLocations.length || selected?.locations == null) {
      return false;
    }

    const selectedIds = new Set(
      selected.locations
        .map((x: ILocationSimple | string) =>
          (typeof x === 'string' ? x : x?.id)?.toLowerCase()
        )
        .filter(Boolean)
    );

    return availableLocations.some((location) => !selectedIds.has(location.id?.toLowerCase()));
  }

  public onLoadingChange(isLoading: boolean) {
    this.isLoading = isLoading;
    if (isLoading) {
      this.isReady = false;
    }
  }

  private _connectSignalR() {
    if (!this._report?.id) {
      return;
    }

    this._signalRSubscription = this._signalRService.commonHub.subscribe((connection) => {
      this._hubConnection = connection;

      if (connection?.state !== HubConnectionState.Connected) {
        return;
      }

      connection.send('viewSalesReport', this._report.id);

      connection.on('salesReportGenerating', (data) => {
        if (this._report.id === data.id) {
          this.searchCard?.enterGeneratingState();
          this.onLoadingChange(true);
          this._cd.detectChanges();
        }
      });

      connection.on('salesReportReady', (data) => {
        if (this._report.id !== data.id) {
          return;
        }

        this._reloadReport();
        this._message.ok({
          message: 'cards.reports.sales_report.ready_notice'
        });
      });

      connection.on('salesReportExpired', (data) => {
        if (this._report.id === data.id) {
          this._reloadReport();
        }
      });
    });
  }

  private _reloadReport() {
    this._router.routeReuseStrategy.shouldReuseRoute = () => false;
    this._router.navigate([
      '/reports/sales',
      this._route.snapshot.paramMap.get('providerId'),
      this._report.id
    ]);
  }
}
